import { createSlice, PayloadAction, Dispatch } from '@reduxjs/toolkit';

import * as customerService from 'services/customerService/customerService';
import * as signupService from 'services/signupService/signupService';
import * as authService from 'services/authService/authService';

import {
  displayApiError,
  displayError
} from 'store/globalErrorState/globalErrorState';
import type { RootState } from 'store';
import { setCustomer } from 'store/customerState';
import { authSelector, validateTokenSuccess } from 'store/authState/authState';
import { ApiError } from 'models/errors/ApiError';
import { CreateCustomerAttributes, CustomerConsentType } from 'models/customer';
import { JsonData } from 'models/jsonApi';
import {
  AccountStatus,
  CompanyDetailsAttributes,
  CompleteProfileAttributes,
  CreateAccountAttributes,
  SignUpState
} from 'models/signup';

export const defaultSignupState: SignUpState = {
  data: {
    email: '',
    password: '',
    confirmedPassword: '',
    firstName: '',
    lastName: '',
    language: '',
    country: '',
    vatNumber: '',
    address: {},
    phoneNumber: {
      dialCode: '',
      number: ''
    },
    accountStatus: {
      loginAccountExists: false,
      loginAccountActive: false,
      customerAccountExists: false,
      customerAccountVerified: false,
      emailVerified: false
    },
    companyName: '',
    companyEmail: '',
    companyPhoneNumber: {
      dialCode: '',
      number: ''
    },
    captchaCode: '',
    captchaElement: null,
    consentType: null,
    emailMarketing: false,
    accountType: null
  },
  isSuccess: false,
  isLoading: false,
  isCredentialsCompleted: false,
  isProfileCompleted: false,
  isCompanyDetailsCompleted: false,
  isVerified: false,
  error: null
};

// Export slice
export const signUpSlice = createSlice({
  name: 'signup',
  initialState: defaultSignupState,
  reducers: {
    resetSignupState() {
      return defaultSignupState;
    },
    submitCredentials: (
      state,
      action: PayloadAction<CreateAccountAttributes>
    ) => {
      state.data.email = action.payload.email;
      state.data.password = action.payload.password ?? '';
      state.data.confirmedPassword = action.payload.confirmedPassword ?? '';
      state.data.emailMarketing = action.payload.emailMarketing;
      state.data.accountType = action.payload.accountType ?? null;
      state.data.accountStatus =
        action.payload.accountStatus ?? defaultSignupState.data.accountStatus;
      state.isCredentialsCompleted = true;
    },
    resetCredentials: (state) => {
      state.data.password = defaultSignupState.data.password;
      state.data.confirmedPassword = defaultSignupState.data.confirmedPassword;
      state.data.accountStatus = defaultSignupState.data.accountStatus;
      state.isCredentialsCompleted = false;
    },
    submitAccountStatus: (state, action: PayloadAction<AccountStatus>) => {
      state.data.accountStatus = action.payload;
    },
    submitCompleteProfile: (
      state,
      action: PayloadAction<CompleteProfileAttributes>
    ) => {
      state.data.firstName = action.payload.firstName;
      state.data.lastName = action.payload.lastName;
      state.data.language = action.payload.language;
      state.data.country = action.payload.country;
      state.data.address = action.payload.address;
      state.data.captchaCode = action.payload.captchaCode;
      state.isProfileCompleted = true;

      const { phoneNumber } = action.payload;
      if (phoneNumber) {
        state.data.phoneNumber = phoneNumber;
      }
    },
    resetCompleteProfile: (state) => {
      state.data.firstName = defaultSignupState.data.firstName;
      state.data.lastName = defaultSignupState.data.lastName;
      state.data.language = defaultSignupState.data.language;
      state.data.country = defaultSignupState.data.country;
      state.data.address = defaultSignupState.data.address;
      state.data.phoneNumber = defaultSignupState.data.phoneNumber;
      state.isProfileCompleted = false;
    },
    submitCompanyDetails: (
      state,
      action: PayloadAction<CompanyDetailsAttributes>
    ) => {
      state.data.companyName = action.payload.companyName;
      state.data.vatNumber = action.payload.vatNumber;
      state.data.companyEmail = action.payload.companyEmail;
      state.data.language = action.payload.language;
      state.data.country = action.payload.country;
      state.data.address = action.payload.address;
      state.isCompanyDetailsCompleted = true;

      const { phoneNumber } = action.payload;
      if (phoneNumber) {
        state.data.phoneNumber = phoneNumber;
      }
    },
    resetCompanyDetails: (state) => {
      state.data.companyName = defaultSignupState.data.companyName;
      state.data.vatNumber = defaultSignupState.data.vatNumber;
      state.data.companyEmail = defaultSignupState.data.companyEmail;
      state.data.language = defaultSignupState.data.language;
      state.data.country = defaultSignupState.data.country;
      state.data.address = defaultSignupState.data.address;
      state.data.phoneNumber = defaultSignupState.data.phoneNumber;
      state.isCompanyDetailsCompleted = false;
    },
    getConsentTypeStart: (state) => {
      state.data.consentType = null;
      state.isLoading = true;
    },
    getConsentTypeSuccess: (
      state,
      action: PayloadAction<CustomerConsentType>
    ) => {
      state.data.consentType = action.payload;
      state.error = null;
      state.isLoading = false;
    },
    getConsentTypeFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    getCaptchaStart: (state) => {
      state.data.captchaElement = null;
    },
    getCaptchaSuccess: (state, action: PayloadAction<string>) => {
      state.data.captchaElement = action.payload;
      state.error = null;
    },
    getCaptchaFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
    },
    signupStart: (state) => {
      state.isLoading = true;
      state.isSuccess = false;
      state.error = null;
    },
    signupSuccess: (state) => {
      state.isSuccess = true;
      state.isLoading = false;
      state.error = null;
    },
    signupFailure: (state, action: PayloadAction<ApiError>) => {
      state.isSuccess = false;
      state.isLoading = false;
      state.error = action.payload;
    },
    verifyAccountStart: (state) => {
      state.isLoading = true;
    },
    // Action boolean tells if the account is already verified.
    verifyAccountSuccess: (state, action: PayloadAction<boolean>) => {
      state.isLoading = false;

      if (action.payload) {
        state.isVerified = true;
      } else {
        state.data.accountStatus.emailVerified = true;
      }
      state.error = null;
    },
    verifyAccountFailure: (state, action: PayloadAction<ApiError>) => {
      state.isLoading = false;
      state.data.accountStatus.emailVerified = false;
      state.error = action.payload;
    },
    sendActivationEmailStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    sendActivationEmailSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    sendActivationEmailFailure: (state, action: PayloadAction<ApiError>) => {
      state.isLoading = false;
      state.error = action.payload;
    }
  }
});

// Export actions
export const {
  resetSignupState,
  resetCredentials,
  resetCompleteProfile,
  submitAccountStatus,
  resetCompanyDetails,
  getConsentTypeStart,
  getConsentTypeSuccess,
  getConsentTypeFailure,
  getCaptchaStart,
  getCaptchaSuccess,
  getCaptchaFailure,
  submitCredentials,
  submitCompleteProfile,
  submitCompanyDetails,
  signupStart,
  signupSuccess,
  signupFailure,
  verifyAccountStart,
  verifyAccountSuccess,
  verifyAccountFailure,
  sendActivationEmailStart,
  sendActivationEmailSuccess,
  sendActivationEmailFailure
} = signUpSlice.actions;

// Export reducer
export const signUpReducer = signUpSlice.reducer;

// Export selector
export const signUpSelector = (state: RootState) => state.signUp;

// Export thunks
export function getConsentType() {
  return async (dispatch: Dispatch) => {
    dispatch(getConsentTypeStart());
    try {
      const consentType = await customerService.getConsentType();
      dispatch(getConsentTypeSuccess(consentType));
    } catch (e: any) {
      dispatch(getConsentTypeFailure(e));
    }
  };
}

export function fetchCaptcha(email: string) {
  return async (dispatch: Dispatch) => {
    dispatch(getCaptchaStart());
    try {
      const svg = await customerService.generateCaptcha(email);
      dispatch(getCaptchaSuccess(svg));
    } catch (e: any) {
      dispatch(getCaptchaFailure(e));
    }
  };
}

export function signUp(data: CreateCustomerAttributes, meta?: JsonData) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      dispatch(signupStart());

      // Create the customer account
      const customer = await signupService.signup(data, meta);

      const state = getState();
      const { session } = authSelector(state);

      // Refetch user info to update state, if login account already existed but no customer account
      if (session.authenticated) {
        const response = await authService.getUserInfo();
        dispatch(validateTokenSuccess(response.data));
      }

      // Update customer state
      dispatch(setCustomer(customer));

      dispatch(signupSuccess());
    } catch (e: any) {
      dispatch(signupFailure(e));
      dispatch(displayApiError(e));
    }
  };
}

export function verifyCustomerAccount(email: string, code: string) {
  return async (dispatch: Dispatch) => {
    dispatch(verifyAccountStart());
    try {
      await signupService.verifyCustomer(email, code);
      dispatch(verifyAccountSuccess(false));
    } catch (error: any) {
      // If verification fails we're checking if the account is previously verified
      try {
        const { emailVerified } =
          await customerService.getAccountStatusByEmail(email);
        // If the account is not verified we assume that the code is invalid.
        if (!emailVerified) {
          dispatch(verifyAccountFailure(error));
          dispatch(displayError('signup.invalid.code'));
          return;
        }
        // If the account is verified we assume that the code has already been used.
        dispatch(verifyAccountSuccess(true));
      } catch (statusError: any) {
        // If the status request fails we act as if the code is invalid.
        dispatch(verifyAccountFailure(statusError));
        dispatch(displayError('signup.invalid.code'));
      }
    }
  };
}

export function resendVerificationEmail(
  email: string,
  noUserAccount: boolean = false,
  iprId?: string
) {
  return async (dispatch: Dispatch) => {
    dispatch(sendActivationEmailStart());

    try {
      if (noUserAccount) {
        await signupService.resendUserVerification(email);
      } else {
        await signupService.resendCustomerVerification(email, iprId);
      }

      dispatch(sendActivationEmailSuccess());
      dispatch(displayError('signup.resend.success'));
    } catch (e: any) {
      dispatch(sendActivationEmailFailure({ ...e }));
      dispatch(displayError('signup.resend.failure'));
    }
  };
}
